import React, { useEffect, useState } from 'react';

import { dataCellClass, dataFrameClass, dataRowClass } from '@/components/admin/SysadminSPA/shared/SPADataTable';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/Table';
import cn from '@/utils/cn';

import OpenerFormatNote from './OpenerFormatNote';
import SectionChanges from './SectionChanges';
import SectionEditLink from './SectionEditLink';
import SectionPreviewLink from './SectionPreviewLink';
import SummaryBanner from './SummaryBanner';
import {
  Tone, tableHeaderCellClass, tableHeaderRowClass, toneText, toneTint,
} from './tone';
import { AnalyzedSection, EditHref, Status } from './types';
import VerdictBadge from './VerdictBadge';

const ROOT = 'tw-mt-8 tw-max-w-5xl tw-transition tw-duration-300 motion-reduce:tw-transition-none';

// A row that says 100% while listing changes reads as a contradiction (rounding, or caption-only
// changes with a perfect text ratio) — cap it at >99.9% whenever real changes exist.
const matchLabel = ({ changes, ratio }: Pick<AnalyzedSection, 'changes' | 'ratio'>) => {
  if (ratio === null) return '—';
  const label = ratio >= 0.9999 ? '100%' : `${(ratio * 100).toFixed(1)}%`;
  if (label === '100%' && changes.some(change => !change.relocated)) return '>99.9%';

  return label;
};

type FilterGroup = 'changed' | 'unchanged' | 'unmatched' | 'missing';
const GROUP_STATUSES: Record<FilterGroup, Status[]> = {
  changed: ['changed'],
  missing: ['online_missing'],
  unchanged: ['no_change'],
  unmatched: ['no_print_match', 'not_compared'],
};
const CHIP_META: { group: FilterGroup; label: string; tone: Tone }[] = [
  { group: 'changed', label: 'to review', tone: 'warning' },
  { group: 'missing', label: 'not online yet', tone: 'accent' },
  { group: 'unchanged', label: 'unchanged', tone: 'success' },
  { group: 'unmatched', label: 'couldn’t match', tone: 'error' },
];
const CHIP = 'tw-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-box-border tw-px-3 tw-py-1.5 '
  + 'tw-text-sm tw-font-bold tw-cursor-pointer';

// Clickable count chips that are both the verdict summary and the table filter. A chip filters to
// its group; clicking the active chip (or All) clears back to everything.
const FilterChips = ({ active, counts, onSelect, total }: {
  active: FilterGroup | null;
  counts: Record<FilterGroup, number>;
  onSelect: (group: FilterGroup | null) => void;
  total: number;
}) => (
  <div className="tw-flex tw-flex-wrap tw-gap-2">
    <button
      aria-pressed={active === null}
      className={cn(CHIP, toneTint.neutral, toneText.neutral,
        active === null ? 'tw-border-spa-accent' : 'tw-border-transparent')}
      type="button"
      onClick={() => onSelect(null)}
    >
      <span className="tw-tabular-nums">{total}</span>&nbsp;all
    </button>
    {CHIP_META.filter(({ group }) => counts[group] > 0).map(({ group, label, tone }) => (
      <button
        key={group}
        aria-pressed={active === group}
        className={cn(CHIP, toneTint[tone], toneText[tone],
          active === group ? 'tw-border-spa-accent' : 'tw-border-transparent')}
        type="button"
        onClick={() => onSelect(active === group ? null : group)}
      >
        <span className="tw-tabular-nums">{counts[group]}</span>&nbsp;{label}
      </button>
    ))}
  </div>
);

// Reset the native button chrome (the tw- prefix keeps preflight from doing it) so the toggle
// reads as an accent text link, like SectionEditLink.
const TOGGLE = 'tw-appearance-none tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer tw-text-sm '
  + 'tw-font-bold tw-whitespace-nowrap tw-text-spa-accent-text hover:tw-underline';

// Changed rows start open (the diffs are the point) but collapse behind their change count when
// the tall ones get in the way.
interface RowProps {
  editHref: EditHref;
  expanded: boolean;
  isLast: boolean;
  onToggle: () => void;
  previewHref: EditHref;
  result: AnalyzedSection;
}

const SectionRow = ({ editHref, expanded, isLast, onToggle, previewHref, result }: RowProps) => {
  const href = editHref(result.sectionId);
  const preview = previewHref(result.sectionId);
  const changeCount = result.changes.length;
  const open = changeCount > 0 && expanded;
  const cell = cn(dataCellClass(isLast || open), 'tw-align-top');

  return (
    <>
      <TableRow className={dataRowClass(0, false)}>
        <TableCell className={cell}>
          <p className="tw-m-0 tw-font-bold tw-text-spa-heading">{result.title}</p>
          <div className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            {result.sectionId !== null && (
              <span className="tw-text-xs tw-text-spa-secondary">ID {result.sectionId}</span>
            )}
            {changeCount > 0 && (
              <button aria-expanded={expanded} className={TOGGLE} type="button" onClick={onToggle}>
                <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>{' '}
                {changeCount} {changeCount === 1 ? 'change' : 'changes'}
              </button>
            )}
            {href && <SectionEditLink href={href} />}
            {preview && <SectionPreviewLink href={preview} />}
            <OpenerFormatNote format={result.openerFormat} />
          </div>
        </TableCell>
        <TableCell className={cn(cell, 'tw-whitespace-nowrap')}>
          <VerdictBadge ratio={result.ratio} status={result.status} />
        </TableCell>
        <TableCell className={cn(cell, 'tw-whitespace-nowrap tw-text-right tw-tabular-nums tw-text-spa-secondary')}>
          {matchLabel(result)}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className={dataRowClass(0, false)}>
          <TableCell className={cn(dataCellClass(isLast), 'tw-pb-5 tw-pt-0')} colSpan={3}>
            <SectionChanges changes={result.changes} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface Props {
  csv: string;
  editHref: EditHref;
  lessonId: number;
  previewHref: EditHref;
  sections: AnalyzedSection[];
}

const Results = ({ csv, editHref, lessonId, previewHref, sections }: Props) => {
  const changedIds = sections.flatMap(s => (s.changes.length > 0 && s.sectionId !== null ? [s.sectionId] : []));

  const [filter, setFilter] = useState<FilterGroup | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set(changedIds));
  const [shown, setShown] = useState(false);
  useEffect(() => setShown(true), []);
  const allExpanded = changedIds.length > 0 && changedIds.every(id => expandedIds.has(id));

  const toggleRow = (id: number | null) => {
    if (id === null) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const counts: Record<FilterGroup, number> = {
    changed: sections.filter(s => GROUP_STATUSES.changed.includes(s.status)).length,
    missing: sections.filter(s => GROUP_STATUSES.missing.includes(s.status)).length,
    unchanged: sections.filter(s => GROUP_STATUSES.unchanged.includes(s.status)).length,
    unmatched: sections.filter(s => GROUP_STATUSES.unmatched.includes(s.status)).length,
  };
  const visible = filter ? sections.filter(s => GROUP_STATUSES[filter].includes(s.status)) : sections;

  const downloadCsv = () => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `lesson-${lessonId}-print-comparison.csv`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  const entrance = shown ? 'tw-opacity-100 tw-translate-y-0' : 'tw-opacity-0 tw-translate-y-1';

  return (
    <div className={cn(ROOT, entrance)}>
      <SummaryBanner results={sections} />

      <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
        <FilterChips active={filter} counts={counts} total={sections.length} onSelect={setFilter} />
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {changedIds.length > 0 && (
            <SPAButton variant="outline" onClick={() => setExpandedIds(allExpanded ? new Set() : new Set(changedIds))}>
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </SPAButton>
          )}
          <SPAButton variant="outline" onClick={downloadCsv}>Download CSV</SPAButton>
        </div>
      </div>

      <div className={cn(dataFrameClass, 'tw-mt-4')}>
        <Table className="tw-w-full tw-border-collapse tw-text-left">
          <TableHeader className={tableHeaderRowClass}>
            <TableRow>
              <TableCell header className={tableHeaderCellClass}>Section</TableCell>
              <TableCell header className={tableHeaderCellClass}>Verdict</TableCell>
              <TableCell header className={cn(tableHeaderCellClass, 'tw-text-right')}>Match</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((result, i) => (
              <SectionRow
                key={result.sectionId ?? result.title}
                editHref={editHref}
                expanded={result.sectionId !== null && expandedIds.has(result.sectionId)}
                isLast={i === visible.length - 1}
                previewHref={previewHref}
                result={result}
                onToggle={() => toggleRow(result.sectionId)}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Results;
